import React from "react";
import { useNavigate } from "react-router-dom";
import { X, Sparkles, ArrowRight, Zap } from "lucide-react";

const PLAN_LABELS = { free: "Free", starter: "Starter", pro: "Pro" };
const TYPE_LABELS = {
  listing: "listing generation",
  cover: "cover project",
  ai_image: "AI image generation",
};
const TYPE_LIMITS = {
  free: { listing: "3 lifetime", cover: "1 lifetime", ai_image: "1 lifetime" },
  starter: { listing: "20 / month", cover: "5 / month", ai_image: "10 / month" },
  pro: { listing: "Unlimited", cover: "Unlimited", ai_image: "30 / month" },
};
const NEXT_PLAN = { free: "starter", starter: "pro", pro: "pro" };
const NEXT_BENEFITS = {
  starter: { listing: "20 / month", cover: "5 / month", ai_image: "10 / month", price: "$19/mo" },
  pro: { listing: "unlimited", cover: "unlimited", ai_image: "30 / month", price: "$39/mo" },
};

/**
 * LimitReachedModal — shown when a feature returns a 403 limit_reached error.
 * The hook that opens it should pass { type, plan } from the API response.
 */
const LimitReachedModal = ({ open, type = "listing", plan = "free", onClose }) => {
  const navigate = useNavigate();
  if (!open) return null;

  const next = NEXT_PLAN[plan] || "starter";
  const benefit = NEXT_BENEFITS[next];
  const isAtTopTier = plan === "pro";

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-[#2C2C2A]/55 backdrop-blur-sm px-4" data-testid="limit-modal">
      <div className="w-full max-w-md bg-white rounded-[12px] border border-[#D3D1C7] shadow-2xl overflow-hidden">
        <div className="px-7 py-7 relative">
          <button
            onClick={onClose}
            className="absolute top-3 right-3 p-2 rounded-md hover:bg-[#F1EFE8] text-[#5F5E5A]"
            aria-label="Close"
            data-testid="limit-close-btn"
          >
            <X className="w-4 h-4" />
          </button>

          <div className="w-12 h-12 rounded-xl bg-[#534AB7]/10 flex items-center justify-center">
            <Zap className="w-5 h-5 text-[#534AB7]" strokeWidth={1.6} />
          </div>

          <h2
            className="mt-5 text-2xl tracking-tight"
            style={{ fontFamily: '"Cormorant Garamond", Georgia, serif', fontWeight: 500 }}
          >
            {isAtTopTier
              ? "You've used your monthly AI image quota"
              : `You've used all your ${TYPE_LABELS[type] || "credit"}s`}
          </h2>
          <p className="mt-2 text-sm text-[#5F5E5A] leading-relaxed">
            {isAtTopTier
              ? "Pro includes 30 AI image generations per month. Your quota resets on the 1st."
              : `Your ${PLAN_LABELS[plan]} plan includes ${TYPE_LIMITS[plan]?.[type] || "limited usage"}. Upgrade to ${PLAN_LABELS[next]} to keep going.`}
          </p>

          {!isAtTopTier && benefit && (
            <div className="mt-5 bg-[#F1EFE8]/70 border border-[#D3D1C7] rounded-[8px] p-4">
              <div className="flex items-center justify-between">
                <span className="text-xs uppercase tracking-[0.18em] text-[#534AB7]">{PLAN_LABELS[next]} plan</span>
                <span className="text-sm font-medium text-[#2C2C2A]" style={{ fontFamily: "Outfit, Inter, sans-serif" }}>
                  {benefit.price}
                </span>
              </div>
              <ul className="mt-3 space-y-1.5 text-sm text-[#2C2C2A]">
                <li className="flex items-center gap-2">
                  <Sparkles className="w-3.5 h-3.5 text-[#534AB7]" /> Listing generations: {benefit.listing}
                </li>
                <li className="flex items-center gap-2">
                  <Sparkles className="w-3.5 h-3.5 text-[#534AB7]" /> Cover projects: {benefit.cover}
                </li>
                <li className="flex items-center gap-2">
                  <Sparkles className="w-3.5 h-3.5 text-[#534AB7]" /> AI images: {benefit.ai_image}
                </li>
              </ul>
            </div>
          )}
        </div>

        <div className="px-7 py-4 border-t border-[#D3D1C7] flex items-center justify-end gap-3 bg-[#FAFAF9]">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-[8px] border border-[#D3D1C7] text-sm hover:bg-[#F1EFE8]"
            data-testid="limit-cancel-btn"
          >
            Maybe later
          </button>
          {!isAtTopTier && (
            <button
              onClick={() => {
                onClose();
                navigate("/pricing");
              }}
              data-testid="limit-upgrade-btn"
              className="px-5 py-2 rounded-[8px] bg-[#534AB7] text-white text-sm font-medium hover:bg-[#3C3489] inline-flex items-center gap-2"
            >
              See upgrade options <ArrowRight className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default LimitReachedModal;
